import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { IsolatedErrorBoundary } from '@client/ErrorBoundary';

type BatchViewErrorBoundaryProps = {
  /**
   * Batch id. A new id remounts the boundary so the next batch can render
   * after this one threw.
   */
  resetKey: string;
  className: string;
  children: React.ReactNode;
};

type CaughtBatchViewError = {
  resetKey: string;
  error: Error;
};

type OnBatchViewError = (error: Error) => void;

/**
 * Catches a render throw in the selected batch. The list stays mounted
 * outside this boundary, so another batch can be selected.
 */
export const BatchViewErrorBoundary: React.FC<BatchViewErrorBoundaryProps> = ({
  resetKey,
  className,
  children,
}) => {
  const [caught, setCaught] = useState<CaughtBatchViewError | null>(null);
  const onError = useCallback<OnBatchViewError>(
    (error) => {
      setCaught({ resetKey, error });
    },
    [resetKey],
  );
  const error = caught?.resetKey === resetKey ? caught.error : null;

  return (
    <IsolatedErrorBoundary
      key={resetKey}
      onError={onError}
      fallback={() => (
        <Box className={className}>
          <Typography variant="body1" color="error">
            This batch could not be shown
          </Typography>
          <Typography variant="body2">
            {error == null
              ? 'Select another batch from the list.'
              : `${error.name}: ${error.message}`}
          </Typography>
          {error != null && (
            <Typography variant="body2">
              Select another batch from the list.
            </Typography>
          )}
        </Box>
      )}
    >
      {children}
    </IsolatedErrorBoundary>
  );
};
